import React from 'react';
// import PropTypes from 'prop-types';
import { path } from 'ramda';
import bindAllActions from '../../common/bindAllActions';
import { isAccountLevelSubscription, isDeviceLevelSubscription, isPlus, isCellularDevice, isHeatStressSubscription } from './index';
import { hasVideo, hasHst } from '../../common/ambient';
import SubscriptionCard from './SubscriptionCard';
import DeviceSubscriptionCard from './DeviceSubscriptionCard';
import SubscriptionTabs from './SubscriptionTabs';
import { useCanShowExternalPurchaseLinks } from '../device/redux/useCanShowExternalPurchaseLinks';
import './SubscriptionsPage.less';

function SubscriptionsPage({ user, device, userActions }) {
  const { canShowExternalPurchaseLinks } = useCanShowExternalPurchaseLinks();
  const userSubs = path(['info', 'payments', 'subscriptions'], user) || [];

  // Filter subscriptions by type and exclude SMS subscriptions
  const accountSubs = userSubs
    .filter(isAccountLevelSubscription)
    .filter(sub => !/awn_sms/.test(sub.productName));
  const deviceSubs = userSubs
    .filter(isDeviceLevelSubscription)
    .filter(sub => !/awn_sms/.test(sub.productName));

  // Filter heat stress subscriptions (account-level)
  const heatStressSubs = accountSubs.filter(sub => isHeatStressSubscription(sub.productName));

  // Group device subscriptions by MAC address
  const deviceSubsByMac = deviceSubs.reduce((acc, sub) => {
    const mac = sub.macAddress;
    if (!acc[mac]) {
      acc[mac] = [];
    }
    acc[mac].push(sub);
    return acc;
  }, {});

  // Get all devices to show cards for all (even those without subscriptions)
  // Filter: only show devices that have lastData (can attach camera) OR are cellular devices
  // Sort: 1) devices with subscriptions, 2) cellular devices, 3) devices with video, 4) rest
  const allDevices = (path(['devices'], device) || [])
    .filter(dev => dev.lastData || isCellularDevice(dev))
    .sort((a, b) => {
      const aHasSub = deviceSubsByMac[a.macAddress]?.length > 0;
      const bHasSub = deviceSubsByMac[b.macAddress]?.length > 0;
      const aIsCellular = isCellularDevice(a);
      const bIsCellular = isCellularDevice(b);
      const aHasVideo = hasVideo(a);
      const bHasVideo = hasVideo(b);

      // Devices with subscriptions first
      if (aHasSub && !bHasSub) return -1;
      if (!aHasSub && bHasSub) return 1;

      // If neither or both have subscriptions, prioritize cellular devices
      if (aIsCellular && !bIsCellular) return -1;
      if (!aIsCellular && bIsCellular) return 1;

      // Then prioritize devices with video
      if (aHasVideo && !bHasVideo) return -1;
      if (!aHasVideo && bHasVideo) return 1;

      return 0; // Keep original order within groups
    });

  // Check if user has any heat stress-capable devices
  const allUserDevices = path(['devices'], device) || [];
  const hasHeatStressDevice = allUserDevices.some(dev => hasHst(dev));

  // Check if user has any active heat stress subscription
  const hasHeatStressSubscription = heatStressSubs.length > 0;

  // Check if user has any Plus subscription
  const hasPlusSubscription = isPlus(user);

  return (
    <div className="payment-subscriptions-page">
      <header className='main'>
        <h1>
          Features & Subscriptions
        </h1>
      </header>
      <SubscriptionTabs currentTab="manage" />
      <div className='page-body'>
        {(accountSubs.length > 0 || !hasPlusSubscription || hasHeatStressDevice) && (
          <div className='block'>
            <h3>Account subscription plans</h3>
            <p>Here you can view, manage and upgrade your account subscription plans.</p>
            <div className='subscriptions-grid'>
              {accountSubs.map((sub, i) => (
                <SubscriptionCard
                  key={i}
                  sub={sub}
                  user={user}
                  device={device}
                  userActions={userActions}
                  index={userSubs.indexOf(sub)}
                  canShowExternalPurchaseLinks={canShowExternalPurchaseLinks}
                />
              ))}
              {!hasPlusSubscription && (
                <SubscriptionCard
                  key="free-card-plus"
                  isFreeCard={true}
                  freeCardType="plus"
                  user={user}
                  device={device}
                  userActions={userActions}
                  canShowExternalPurchaseLinks={canShowExternalPurchaseLinks}
                />
              )}
              {hasHeatStressDevice && !hasHeatStressSubscription && (
                <SubscriptionCard
                  key="free-card-heat-stress"
                  isFreeCard={true}
                  freeCardType="heat-stress"
                  user={user}
                  device={device}
                  userActions={userActions}
                  canShowExternalPurchaseLinks={canShowExternalPurchaseLinks}
                />
              )}
            </div>
            {canShowExternalPurchaseLinks && (
              <>
                <br/>
                <br/>
                <h3>Share AWN+ with anyone</h3>
                <p>From professional teams to families - AWN Gift Codes delivers the plan that fits their needs.
    Buy a gift subscription for the plan that's right for them - AWN+ Individual or AWN+ Teams.</p>
                <div className='btns'>
                  <a className='btn btn-primary' target='_blank' rel='noreferrer' href='https://ambientweather.com/awn-plus-annual-subscription'>Buy Gift Code</a>
                </div>
              </>
            )}
          </div>
        )}

        {allDevices.length > 0 && (
          <div className='block'>
            <h3>Device subscription plans</h3>
            <p>Here you can view, manage the subscription plans available for your weather station devices</p>
            <div className='subscriptions-grid'>
              {allDevices.map((dev) => {
                const macAddress = dev.macAddress;
                const subs = deviceSubsByMac[macAddress] || [];
                return (
                  <DeviceSubscriptionCard
                    key={macAddress}
                    macAddress={macAddress}
                    subscriptions={subs}
                    user={user}
                    device={device}
                    userActions={userActions}
                  />
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default bindAllActions(SubscriptionsPage);

SubscriptionsPage.propTypes = {};
SubscriptionsPage.defaultProps = {};

SubscriptionsPage.displayName = 'SubscriptionsPage'
